// Utility functions for formatting movie data

/**
 * Format runtime from minutes to "Xh Ymin" format
 * @param {number} runtime - Runtime in minutes
 * @returns {string} Formatted runtime string
 */
export const formatRuntime = (runtime) => {
  if (!runtime || runtime <= 0) return 'N/A';
  const hours = Math.floor(runtime / 60);
  const minutes = runtime % 60;
  return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
};

/**
 * Format release date to readable format
 * @param {string} releaseDate - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string (e.g., "Mar 15, 2024")
 */
export const formatReleaseDate = (releaseDate) => {
  if (!releaseDate) return 'N/A';
  const date = new Date(releaseDate);
  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format financial values (revenue, budget) to readable format
 * @param {number} value - Financial value
 * @returns {string} Formatted financial string (e.g., "$123.5M", "$1.2B")
 */
export const formatFinancialValue = (value) => {
  if (!value || value === 0) return 'N/A';

  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }

  return `$${value.toLocaleString()}`;
};

/**
 * Calculate Return on Investment (ROI) percentage
 * @param {number} revenue - Movie revenue
 * @param {number} budget - Movie budget
 * @returns {string} Formatted ROI percentage or 'N/A'
 */
export const calculateROI = (revenue, budget) => {
  if (revenue <= 0 || budget <= 0) return 'N/A';
  const roi = Math.round(((revenue - budget) / budget) * 100);
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(roi)}%`;
};

/**
 * Format large numbers with K, M, B suffixes
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (!num || num === 0) return '0';

  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`;
  } else if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }

  return num.toLocaleString();
};
